// src/shared/utils/pwaInstallPromptBridge.js
// Captures the browser install prompt as early as possible so the login screen
// can trigger it immediately when the user taps Download.

let deferredInstallPrompt = null;
let installedInThisSession = false;
const listeners = new Set();

function emit() {
  const snapshot = getPwaInstallState();
  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (_) {}
  });
}

export function detectInstallPlatform() {
  if (typeof navigator === 'undefined') return 'other';

  const nav = navigator;
  const ua = String(nav.userAgent || '');
  const uaPlatform = String(nav.userAgentData?.platform || nav.platform || '');

  // iPadOS can report itself as Macintosh.
  const isIPadDesktopUa =
    /Mac/i.test(uaPlatform) &&
    Number(nav.maxTouchPoints || 0) > 1;

  if (
    /iPad|iPhone|iPod/i.test(ua) ||
    isIPadDesktopUa
  ) {
    return 'ios';
  }

  if (
    /Android/i.test(ua) ||
    /Android/i.test(uaPlatform)
  ) {
    return 'android';
  }

  if (
    /Windows/i.test(ua) ||
    /Win/i.test(uaPlatform)
  ) {
    return 'windows';
  }

  return 'other';
}

export function isStandalonePwa() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  try {
    if (window.navigator.standalone === true) return true;
  } catch (_) {}

  const displayModes = [
    'standalone',
    'fullscreen',
    'minimal-ui',
    'window-controls-overlay',
  ];

  try {
    if (
      displayModes.some((mode) =>
        window.matchMedia?.(`(display-mode: ${mode})`)?.matches
      )
    ) {
      return true;
    }
  } catch (_) {}

  // Android Trusted Web Activity / app wrapper fallback.
  try {
    if (String(document.referrer || '').startsWith('android-app://')) {
      return true;
    }
  } catch (_) {}

  return false;
}

export function getPwaInstallState() {
  return {
    prompt: deferredInstallPrompt,
    installedInThisSession,
  };
}

export function takeDeferredInstallPrompt() {
  const prompt = deferredInstallPrompt;
  deferredInstallPrompt = null;
  emit();
  return prompt;
}

export function subscribePwaInstallState(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);

  try {
    listener(getPwaInstallState());
  } catch (_) {}

  return () => {
    listeners.delete(listener);
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    const platform = detectInstallPlatform();

    // The requested direct install flow is only for Windows / Android.
    // Do not suppress the browser's native behavior on unrelated platforms.
    if (platform !== 'windows' && platform !== 'android') return;

    event.preventDefault();
    deferredInstallPrompt = event;
    installedInThisSession = false;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    installedInThisSession = true;
    emit();
  });
}
